/**
 * Extensions Hooks - TanStack Query implementation
 *
 * Server state management for extension data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { queryKeys, invalidateQueries } from '@/lib/query-keys'
import { toast } from 'sonner'
import type { Extension } from '@/lib/types'

// Types
export interface ExtensionParams {
  status?: string
  category?: string
  search?: string
  page?: number
}

export interface ApproveExtensionData {
  id: string
  status: 'approved' | 'rejected'
  reviewNotes?: string
}

export interface CreateExtensionData {
  name: string
  version: string
  description?: string
  developerId: string
  category?: string
}

export interface UpdateExtensionData {
  name?: string
  version?: string
  description?: string
  category?: string
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch all extensions with optional filters
 */
export function useExtensions(params: ExtensionParams = {}) {
  return useQuery<Extension[]>({
    queryKey: [...queryKeys.extensions.lists(), params] as const,
    queryFn: async () => {
      const response = await api.listExtensions(params)
      return response.data || []
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Fetch extension detail by ID
 */
export function useExtension(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.extensions.detail(Number(id)) : ['extension', 'null'],
    queryFn: async () => {
      if (!id) return null
      const response = await api.getExtension(id)
      return response.data || null
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch pending extensions (approval queue)
 */
export function usePendingExtensions() {
  return useQuery<Extension[]>({
    queryKey: queryKeys.extensions.pending(),
    queryFn: async () => {
      const response = await api.listExtensions({ status: 'pending' })
      return response.data || []
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 15 * 1000, // Poll every 15s for pending items
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Submit new extension for approval
 */
export function useCreateExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateExtensionData) => {
      return api.createExtension(data)
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'extensions')
      toast.success('Extension submitted for approval')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to submit extension')
    },
  })
}

/**
 * Approve or reject extension
 */
export function useApproveExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status, reviewNotes }: ApproveExtensionData) => {
      return api.approveExtension(id, { status, reviewNotes })
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'extensions')
      toast.success('Extension updated')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update extension')
    },
  })
}

/**
 * Update extension
 */
export function useUpdateExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExtensionData }) => {
      return api.updateExtension(id, data)
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'extensions')
      toast.success('Extension updated')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update extension')
    },
  })
}

/**
 * Delete extension
 */
export function useDeleteExtension() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      return api.deleteExtension(id)
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'extensions')
      toast.success('Extension deleted')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete extension')
    },
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to refetch all extension queries
 */
export function useRefetchExtensions() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.extensions.root,
    })
  }
}
